import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import SiteHeader from "@/components/SiteHeader";
import ScanReveal from "@/components/ScanReveal";
import ShareButton from "@/components/ShareButton";
import { AssociationsAPI, CompaniesAPI } from "@/lib/api";
import { themeVars, titleStyles } from "@/lib/appearance";
import { Calendar, Users, BarChart3 } from "lucide-react";
import { toPlayableVideoUrl, videoPosterUrl } from "@/lib/video";
import { transparentLogoUrl } from "@/lib/logo";

const ease = [0.22, 1, 0.36, 1];
const GOLD = "#D4AF37";

const SectionHeading = ({ children, right }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.55, ease }}
    className="flex items-end justify-between mb-6"
  >
    <h2 className="font-display text-3xl md:text-5xl font-semibold uppercase tracking-tight">
      {children}
    </h2>
    {right}
  </motion.div>
);

const ytEmbed = (url) => {
  const m = String(url || "").match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/,
  );
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

export default function OrgProfile({ type = "association" }) {
  const { slug } = useParams();
  const [org, setOrg] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const apiRef = type === "company" ? CompaniesAPI : AssociationsAPI;

  useEffect(() => {
    apiRef.get(slug).then(setOrg).catch(() => setNotFound(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, type]);

  if (notFound)
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center gap-4 font-body">
        <div className="text-zinc-500">Profil introuvable</div>
        <Link to="/" className="text-[#D4AF37] underline">Accueil</Link>
      </div>
    );
  if (!org)
    return (
      <div className="min-h-screen bg-[#050505] text-zinc-500 font-body flex items-center justify-center">
        Chargement...
      </div>
    );

  const accent = org.primary_color || GOLD;
  const ap = org.appearance || {};
  const titleCfg = titleStyles(ap, "xl");
  const employees = (org.employees || []).filter((e) => e.visible !== false);
  const showEmployees = type === "company" && org.employees_enabled && employees.length > 0;
  const milestones = (org.milestones || []).filter((m) => (m.title || "").trim() || (m.description || "").trim());
  const achievements = (org.achievements || []).filter((a) => (a.title || "").trim() || (a.description || "").trim());
  const gallery = (org.gallery || []).filter((g) => (g.url || "").trim());
  const videos = (org.highlights || []).filter((h) => (h.url || "").trim());
  const committee = (org.committee || []).filter((m) => (m.first_name || "").trim() || (m.last_name || "").trim());
  const showCommittee = type !== "company" && org.committee_enabled && committee.length > 0;
  const standings = [...(org.internal_standings || [])].sort((a, b) => (b.points || 0) - (a.points || 0));
  const showStandings = type === "company" && org.standings_enabled && standings.length > 0;
  const label = type === "company" ? "Entreprise" : "Association";

  return (
    <ScanReveal slug={org.slug} kind={type === "company" ? "company" : "association"}>
      <div className="event-theme min-h-screen bg-[#050505] text-white" style={themeVars(ap)}>
        <SiteHeader playerName={org.name} accentColor={accent} />

        {/* Hero */}
        <section className="relative overflow-hidden">
          {org.cover_image_url && (
            <div className="absolute inset-0">
              <img src={org.cover_image_url} alt="" className="w-full h-full object-cover opacity-25" />
              <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/80 to-[#050505]" />
            </div>
          )}
          <div className="relative max-w-5xl mx-auto px-4 md:px-8 py-16 md:py-24 flex flex-col items-start gap-5">
            <div className="flex items-center gap-4 flex-wrap">
              {org.logo_url && (
                <img
                  src={transparentLogoUrl(org.logo_url)}
                  alt={org.name}
                  className="w-20 h-20 md:w-28 md:h-28 object-contain"
                  data-testid="org-logo"
                />
              )}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.4em] mb-2" style={{ color: accent }}>
                  {label}{org.founded_year ? ` · Depuis ${org.founded_year}` : ""}
                </div>
                {!ap.hide_title && (
                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.85, ease }}
                    className={`font-display ${titleCfg.sizeClass} font-semibold uppercase tracking-tighter leading-[0.9] text-gradient-gold`}
                    style={titleCfg.style}
                    data-testid="org-name"
                  >
                    {org.name}
                  </motion.h1>
                )}
                {org.subtitle && (
                  <div className="text-zinc-400 font-body mt-2">{org.subtitle}</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <ShareButton kind={type === "company" ? "company" : "association"} slug={org.slug} title={org.name} accent={accent} />
            </div>
          </div>
        </section>

        {/* Histoire / Présentation */}
        {(org.history || org.activity) && (
          <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-14" data-testid="org-history">
            <SectionHeading>{type === "company" ? "Présentation" : "Notre histoire"}</SectionHeading>
            {org.activity && (
              <div className="mb-4">
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: accent }}>
                  Activité
                </span>
                <p className="text-zinc-300 font-body mt-1 whitespace-pre-line">{org.activity}</p>
              </div>
            )}
            {org.history && (
              <p className="text-zinc-300 font-body leading-relaxed whitespace-pre-line max-w-3xl">
                {org.history}
              </p>
            )}
          </section>
        )}

        {/* Moments importants */}
        {milestones.length > 0 && (
          <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-14" data-testid="org-milestones">
            <SectionHeading
              right={
                <span className="text-overline" style={{ color: accent }}>
                  <Calendar className="w-3 h-3 inline mr-1" /> {milestones.length} moments
                </span>
              }
            >
              Moments importants
            </SectionHeading>
            <div className="relative pl-6 border-l border-white/10 space-y-8">
              {milestones.map((m, i) => (
                <div key={m.id || i} className="relative">
                  <span
                    className="absolute -left-[31px] top-1 w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: accent }}
                  />
                  <div className="font-display text-sm tabular-nums" style={{ color: accent }}>{m.date}</div>
                  <div className="font-display text-xl font-semibold uppercase tracking-tight">{m.title}</div>
                  {m.description && <p className="text-zinc-400 font-body text-sm mt-1">{m.description}</p>}
                  {m.image_url && (
                    <img src={m.image_url} alt={m.title} className="mt-3 rounded-lg max-h-56 object-cover" />
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Réalisations */}
        {achievements.length > 0 && (
          <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-14" data-testid="org-achievements">
            <SectionHeading>Réalisations</SectionHeading>
            <div className="grid sm:grid-cols-2 gap-3">
              {achievements.map((a, i) => (
                <div key={a.id || i} className="bg-[#111216] border border-white/10 rounded-xl p-4">
                  {a.date && <div className="text-xs font-display" style={{ color: accent }}>{a.date}</div>}
                  <div className="font-display font-semibold uppercase tracking-tight">{a.title}</div>
                  {a.description && <p className="text-zinc-400 font-body text-sm mt-1">{a.description}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Comité et membres (associations) */}
        {showCommittee && (
          <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-14" data-testid="org-committee">
            <SectionHeading
              right={
                <span className="text-overline" style={{ color: accent }}>
                  <Users className="w-3 h-3 inline mr-1" /> {committee.length} membres
                </span>
              }
            >
              Comité et membres
            </SectionHeading>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {committee.map((m, i) => {
                const fullName = [m.first_name, m.last_name].filter(Boolean).join(" ");
                return (
                  <div key={m.id || i} className="bg-[#111216] border border-white/10 rounded-xl p-4 text-center" data-testid={`org-committee-card-${i}`}>
                    {m.photo_url ? (
                      <img src={m.photo_url} alt={fullName} className="w-20 h-20 rounded-full object-cover mx-auto mb-3 border border-white/10" />
                    ) : (
                      <div className="w-20 h-20 rounded-full mx-auto mb-3 bg-white/5 flex items-center justify-center font-display text-2xl" style={{ color: accent }}>
                        {(fullName || "?").charAt(0)}
                      </div>
                    )}
                    <div className="font-display font-semibold uppercase tracking-tight">{fullName}</div>
                    {m.role && m.role.trim() && <div className="text-[11px] uppercase tracking-[0.18em] mt-0.5" style={{ color: accent }}>{m.role}</div>}
                    {m.description && <p className="text-zinc-500 font-body text-xs mt-2">{m.description}</p>}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Employés (Option A) */}
        {showEmployees && (
          <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-14" data-testid="org-employees">
            <SectionHeading
              right={
                <span className="text-overline" style={{ color: accent }}>
                  <Users className="w-3 h-3 inline mr-1" /> {employees.length} membres
                </span>
              }
            >
              Notre équipe
            </SectionHeading>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {employees.map((e, i) => (
                <div key={e.id || i} className="bg-[#111216] border border-white/10 rounded-xl p-4 text-center">
                  {e.photo_url ? (
                    <img src={e.photo_url} alt={e.name} className="w-20 h-20 rounded-full object-cover mx-auto mb-3 border border-white/10" />
                  ) : (
                    <div className="w-20 h-20 rounded-full mx-auto mb-3 bg-white/5 flex items-center justify-center font-display text-2xl" style={{ color: accent }}>
                      {(e.name || "?").charAt(0)}
                    </div>
                  )}
                  <div className="font-display font-semibold uppercase tracking-tight">{e.name}</div>
                  {e.role && <div className="text-[11px] uppercase tracking-[0.18em] mt-0.5" style={{ color: accent }}>{e.role}</div>}
                  {e.description && <p className="text-zinc-500 font-body text-xs mt-2">{e.description}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Classement interne (Option B) */}
        {showStandings && (
          <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-14" data-testid="org-standings">
            <SectionHeading
              right={
                <span className="text-overline" style={{ color: accent }}>
                  <BarChart3 className="w-3 h-3 inline mr-1" /> {standings.length} participants
                </span>
              }
            >
              {org.standings_title || "Challenge interne"}
            </SectionHeading>
            <div className="bg-[#111216] border border-white/10 rounded-xl overflow-hidden">
              <div className="grid grid-cols-[48px,1fr,80px] gap-2 px-4 py-3 border-b border-white/10 text-[11px] font-display font-bold uppercase tracking-[0.18em] text-zinc-500">
                <div>#</div><div>Participant</div><div className="text-right">PTS</div>
              </div>
              {standings.map((r, i) => (
                <div key={r.id || i} className="grid grid-cols-[48px,1fr,80px] gap-2 px-4 py-3 border-b border-white/5 last:border-0 items-center">
                  <div className="font-display font-black" style={{ color: i < 3 ? accent : "#71717A" }}>{i + 1}</div>
                  <div className="font-display uppercase tracking-tight font-semibold">
                    {r.name}
                    {r.team && <span className="text-zinc-500 font-body text-xs normal-case ml-2">{r.team}</span>}
                  </div>
                  <div className="text-right font-display tabular-nums font-bold" style={{ color: accent }}>{r.points}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Galerie */}
        {gallery.length > 0 && (
          <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-14" data-testid="org-gallery">
            <SectionHeading>Galerie</SectionHeading>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {gallery.map((g, i) => (
                <figure key={g.id || i} className="rounded-xl overflow-hidden border border-white/10">
                  <img src={g.url} alt={g.caption || ""} className="w-full h-44 object-cover hover:scale-105 transition-transform duration-500" />
                  {g.caption && <figcaption className="text-xs text-zinc-500 font-body p-2">{g.caption}</figcaption>}
                </figure>
              ))}
            </div>
          </section>
        )}

        {/* Vidéos */}
        {videos.length > 0 && (
          <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-20" data-testid="org-videos">
            <SectionHeading>Vidéos</SectionHeading>
            <div className="grid md:grid-cols-2 gap-4">
              {videos.map((h, i) => {
                const yt = ytEmbed(h.url);
                return (
                  <div key={h.id || i} className="rounded-xl overflow-hidden border border-white/10 bg-[#111216]">
                    {yt ? (
                      <iframe src={yt} title={h.title} className="w-full aspect-video" allowFullScreen />
                    ) : (
                      <video src={toPlayableVideoUrl(h.url)} controls playsInline preload="metadata" className="w-full aspect-video bg-black" poster={h.poster || videoPosterUrl(h.url) || undefined} />
                    )}
                    {h.title && <div className="p-3 font-display uppercase tracking-tight font-semibold text-sm">{h.title}</div>}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <footer className="border-t border-white/5 py-8 text-center text-zinc-600 font-body text-xs">
          {org.name} · Profil connecté Laser Tech Création
        </footer>
      </div>
    </ScanReveal>
  );
}
